import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Hammer, FileSpreadsheet, BookOpen, TrendingUp, ArrowUpRight, FileText, Users, Wallet, AlertTriangle, CalendarClock, ShieldAlert } from "lucide-react";
import { statusLabel, statusBadge } from "../lib/jobStatus";
import { useAuth } from "../context/AuthContext";
import { FinanceBlocks } from "../components/dashboard/FinanceBlocks";
import { isTech } from "../lib/roles";
import { Attendance } from "../components/Attendance";

const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";


export default function Dashboard() {
    const { user } = useAuth();
    const tech = isTech(user);
    const [data, setData] = useState(null);
    const [cf, setCf] = useState(null);
    const [today, setToday] = useState(null);

    useEffect(() => {
        if (tech) {
            api.get("/dashboard").then((d) => setData(d.data));
        } else {
            Promise.all([api.get("/dashboard"), api.get("/dashboard/cashflow")])
                .then(([d, c]) => { setData(d.data); setCf(c.data); });
        }
        api.get("/dashboard/today").then((r) => setToday(r.data)).catch(() => {});
    }, [tech]);

    if (tech) return data ? <TechDashboard data={data} today={today} name={user?.name} /> : <div className="p-10 text-slate-500 uppercase text-xs tracking-widest">Načítání přehledu…</div>;

    if (!data || !cf) {
        return <div className="p-10 text-slate-500 uppercase text-xs tracking-widest">Načítání přehledu…</div>;
    }

    const maxMonthly = Math.max(1, ...cf.monthly.map(m => Math.max(m.income, m.expected_costs)));

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="dashboard-page">
            <div className="flex items-end justify-between mb-10">
                <div>
                    <div className="label-eyebrow mb-2">Dashboard</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Přehled firmy</h1>
                </div>
                <Link to="/zakazky/nova" data-testid="quick-new-job"
                      className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    + Nová zakázka
                </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Stat icon={Hammer} label="Aktivní zakázky" value={data.jobs_active} sub={`celkem ${data.jobs_total}`} testid="stat-active-jobs" />
                <Stat icon={FileSpreadsheet} label="Rozpočty" value={data.budgets_total} sub={fmt(data.budgets_value)} testid="stat-budgets" />
                <Stat icon={FileText} label="Faktury" value={data.invoices_total} sub={fmt(data.invoices_value)} testid="stat-invoices" />
                <Stat icon={Wallet} label="K inkasu" value={fmt(data.invoices_unpaid)} sub={`zaplaceno ${fmt(data.invoices_paid)}`} big testid="stat-unpaid" />
            </div>

            {/* Po splatnosti – vysoký kontrast */}
            {(data.overdue_count > 0) && (
                <div className="bg-red-800 text-white border-l-8 border-red-950 px-5 py-4 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="overdue-banner">
                    <div className="flex items-center gap-4">
                        <AlertTriangle className="w-7 h-7 text-red-100 shrink-0" />
                        <div>
                            <div className="text-[11px] uppercase tracking-widest font-bold text-red-100">Po splatnosti · {data.overdue_count} {data.overdue_count === 1 ? "faktura" : data.overdue_count < 5 ? "faktury" : "faktur"}</div>
                            <div className="font-display text-3xl sm:text-4xl font-black tabular-data text-white" data-testid="overdue-amount">{fmt(data.overdue_value)}</div>
                        </div>
                    </div>
                    <Link to="/faktury" className="text-sm font-bold text-white hover:text-red-100 uppercase tracking-wider border border-red-200/60 px-4 py-2">Zobrazit →</Link>
                </div>
            )}

            {/* Today & Tomorrow widget */}
            {today && (today.events.length > 0 || today.tasks.length > 0 || today.overdue_count > 0 || today.payables_count > 0) && (
                <div className="bg-slate-900 text-white border-l-4 border-orange-500 mb-6" data-testid="today-widget">
                    <div className="px-6 py-3 border-b border-slate-800 flex items-center gap-2">
                        <CalendarClock className="w-4 h-4 text-orange-400" />
                        <span className="font-display font-bold uppercase tracking-wider text-sm">Dnes a zítra</span>
                    </div>
                    <div className={`p-5 grid grid-cols-1 md:grid-cols-3 ${today.payables_count > 0 ? "xl:grid-cols-4" : ""} gap-5 text-sm`}>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-2">Události</div>
                            {today.events.length === 0 ? <div className="text-slate-500 text-xs">Žádné události</div> :
                                today.events.map((e) => (
                                    <Link key={e.id} to="/planovac" className="block mb-1.5 hover:text-orange-300">
                                        <span className={`text-[10px] font-bold uppercase mr-1.5 ${e.day === "today" ? "text-orange-400" : "text-slate-400"}`}>
                                            {e.day === "today" ? "Dnes" : "Zítra"}{!e.all_day && e.start_datetime?.length > 10 ? ` ${e.start_datetime.slice(11, 16)}` : ""}
                                        </span>
                                        {e.title}{e.location ? <span className="text-slate-400 text-xs"> · {e.location}</span> : ""}
                                    </Link>
                                ))}
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-2">Úkoly s termínem</div>
                            {today.tasks.length === 0 ? <div className="text-slate-500 text-xs">Žádné úkoly</div> :
                                today.tasks.map((t) => (
                                    <Link key={t.id} to="/ukoly" className="block mb-1.5 hover:text-orange-300" data-testid={`today-task-${t.id}`}>
                                        {t.overdue && <span className="text-[10px] font-bold uppercase text-red-400 mr-1.5">Po termínu!</span>}
                                        {t.visibility === "firemni" && <span className="text-[10px] font-bold uppercase text-slate-400 mr-1.5">Firemní</span>}
                                        {t.visibility === "soukromy" && <span className="text-[10px] font-bold uppercase text-sky-400 mr-1.5">Soukromý</span>}
                                        {t.title}{t.assignee_name ? <span className="text-slate-400 text-xs"> → {t.assignee_name}</span> : ""}
                                    </Link>
                                ))}
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-2">Po splatnosti ({today.overdue_count})</div>
                            {today.overdue_invoices.length === 0 ? <div className="text-slate-500 text-xs">Vše uhrazeno ✓</div> :
                                today.overdue_invoices.map((i) => (
                                    <Link key={i.id} to={`/faktury/${i.id}`} className="block mb-1.5 hover:text-orange-300">
                                        <span className="font-mono text-xs text-orange-400 mr-1.5">{i.number}</span>
                                        {i.client_name} <span className="text-red-400 text-xs">({i.days_overdue} dní)</span>
                                    </Link>
                                ))}
                        </div>
                        {today.payables_count > 0 && (
                            <div data-testid="payables-widget">
                                <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">Zaplatit dodavatelům ({today.payables_count}) · {fmt(today.payables_total)}</div>
                                {today.payables.map((p) => (
                                    <Link key={p.id} to="/prijate-faktury" className="block mb-1.5 hover:text-orange-300">
                                        <span className={`text-[10px] font-bold uppercase mr-1.5 ${p.days_to_due < 0 ? "text-red-400" : "text-amber-400"}`}>
                                            {p.days_to_due < 0 ? `${-p.days_to_due} dní po` : p.days_to_due === 0 ? "Dnes" : `za ${p.days_to_due} dní`}
                                        </span>
                                        {p.supplier_name} <span className="text-slate-400 text-xs">{fmt(p.total)}</span>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FinanceBlocks fmt={fmt} />

            {data.warranty_alerts && data.warranty_alerts.length > 0 && (
                <div className="bg-orange-50 border-l-4 border-orange-600 p-4 mb-6" data-testid="warranty-alert-banner">
                    <div className="flex items-center gap-3 mb-2">
                        <ShieldAlert className="w-5 h-5 text-orange-700" />
                        <div className="font-semibold text-orange-900">Záruky končící do 30 dní ({data.warranty_alerts_count})</div>
                    </div>
                    <div className="ml-8 space-y-1">
                        {data.warranty_alerts.map((w) => (
                            <Link key={w.job_id} to={`/zakazky/${w.job_id}`} className="block text-sm text-orange-800 hover:text-orange-900">
                                <span className="font-semibold">{w.title}</span> · {w.client_name} · končí {w.expires_at} <span className="text-orange-600">({w.days_left} dní)</span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
                <Stat icon={Users} label="Klienti" value={data.clients_count} sub="v číselníku" testid="stat-clients" />
                <Stat icon={BookOpen} label="Položek v ceníku" value={data.catalog_count} sub="šablony" testid="stat-catalog" />
                <Stat icon={TrendingUp} label="Náklady zakázek" value={fmt(data.total_costs)} sub="suma" big testid="stat-costs" />
                <Stat icon={Hammer} label="Dokončené zakázky" value={data.jobs_done} sub="celkem" testid="stat-done-jobs" />
            </div>

            {/* Cashflow */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white border border-slate-200" data-testid="cashflow-widget">
                    <div className="px-6 py-4 border-b-2 border-slate-900 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <CalendarClock className="w-5 h-5 text-orange-600" />
                            <div>
                                <div className="label-eyebrow">Cash-flow výhled</div>
                                <div className="font-display text-lg font-bold text-slate-900">90 dní dopředu</div>
                            </div>
                        </div>
                    </div>
                    <div className="p-6">
                        {cf.monthly.length === 0 ? (
                            <div className="text-center py-10 text-slate-400 text-sm">Žádné nadcházející platby ani aktivní zakázky.</div>
                        ) : (
                            <div className="space-y-3">
                                {cf.monthly.slice(0, 4).map((m) => (
                                    <div key={m.month}>
                                        <div className="flex items-center justify-between text-xs mb-1.5">
                                            <span className="font-semibold uppercase tracking-wider text-slate-700">{formatMonth(m.month)}</span>
                                            <span className="tabular-data text-slate-600">+{fmt(m.income)} <span className="text-slate-400 mx-1">/</span> -{fmt(m.expected_costs)}</span>
                                        </div>
                                        <div className="h-2 bg-slate-100 relative overflow-hidden flex">
                                            <div className="bg-green-500 h-full" style={{ width: `${(m.income / maxMonthly) * 100}%` }}></div>
                                        </div>
                                        <div className="h-1.5 bg-slate-100 relative overflow-hidden flex mt-0.5">
                                            <div className="bg-red-400 h-full" style={{ width: `${(m.expected_costs / maxMonthly) * 100}%` }}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="space-y-3">
                    <MiniStat label="Příští 30 dní" value={fmt(cf.next_30_days_income)} accent />
                    <MiniStat label="Příštích 60 dní" value={fmt(cf.next_60_days_income)} />
                    <MiniStat label="Příštích 90 dní" value={fmt(cf.next_90_days_income)} />
                    {cf.craftsmen_balance_to_pay > 0 && <MiniStat label="Řemeslníci — nedoplatky" value={fmt(cf.craftsmen_balance_to_pay)} danger />}
                    {cf.payables_30_days > 0 && <MiniStat label="Dodavatelé — splatné do 30 dní" value={fmt(cf.payables_30_days)} danger />}
                    {cf.net_30_days !== undefined && (
                        <div className={`border-2 p-3 ${cf.net_30_days >= 0 ? "border-emerald-400 bg-emerald-50" : "border-red-400 bg-red-50"}`} data-testid="net-30-days">
                            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Čistý cashflow 30 dní</div>
                            <div className={`font-display text-lg font-black ${cf.net_30_days >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(cf.net_30_days)}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">Příjmy − nedoplatky řemeslníkům − dodavatelé</div>
                        </div>
                    )}
                    {cf.overdue_total > 0 && <MiniStat label="Po splatnosti (vymáhat zvlášť)" value={fmt(cf.overdue_total)} danger />}
                    {cf.planned_income_open_jobs > 0 && <MiniStat label="Plánované příjmy rozpracovaných zakázek (ještě nefakturováno)" value={fmt(cf.planned_income_open_jobs)} />}
                </div>
            </div>

            {/* Recent jobs */}
            <div className="bg-white border border-slate-200">
                <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                    <div>
                        <div className="label-eyebrow">Poslední zakázky</div>
                        <h2 className="font-display text-xl font-bold text-slate-900 mt-1">Nejnovější aktivita</h2>
                    </div>
                    <Link to="/zakazky" data-testid="link-all-jobs" className="text-xs font-semibold uppercase tracking-wider text-orange-600 hover:text-orange-700 flex items-center gap-1">
                        Vše <ArrowUpRight className="w-3 h-3" />
                    </Link>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                                <th className="px-6 py-3 font-semibold">Název</th>
                                <th className="px-6 py-3 font-semibold">Klient</th>
                                <th className="px-6 py-3 font-semibold">Stav</th>
                                <th className="px-6 py-3 font-semibold text-right">Náklady</th>
                            </tr>
                        </thead>
                        <tbody className="tabular-data">
                            {data.recent_jobs.length === 0 && (
                                <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-400">Zatím žádné zakázky.</td></tr>
                            )}
                            {data.recent_jobs.map((j, idx) => {
                                const total = (j.costs || []).reduce((s, c) => s + (c.total || c.quantity * c.unit_price || 0), 0);
                                return (
                                    <tr key={j.id} className={`border-b border-slate-100 hover:bg-slate-50 ${idx % 2 ? "bg-slate-50/40" : ""}`}>
                                        <td className="px-6 py-3">
                                            <Link to={`/zakazky/${j.id}`} className="font-medium text-slate-900 hover:text-orange-600">{j.title}</Link>
                                        </td>
                                        <td className="px-6 py-3 text-slate-600">{j.client_name || "—"}</td>
                                        <td className="px-6 py-3">
                                            <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${statusBadge(j.status)}`}>
                                                {statusLabel(j.status)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-right font-medium">{fmt(total)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function formatMonth(s) {
    const [y, m] = s.split("-");
    const names = ["Led", "Úno", "Bře", "Dub", "Kvě", "Čvn", "Čvc", "Srp", "Zář", "Říj", "Lis", "Pro"];
    return `${names[Number(m) - 1]} ${y}`;
}

function Stat({ icon: Icon, label, value, sub, big, testid }) {
    return (
        <div data-testid={testid} className="bg-white border border-slate-200 p-5 sharp-shadow hover:border-slate-300 transition-colors">
            <div className="flex items-start justify-between mb-3">
                <div className="label-eyebrow">{label}</div>
                <Icon className="w-4 h-4 text-orange-600" strokeWidth={2} />
            </div>
            <div className={`font-display ${big ? "text-2xl" : "text-3xl"} font-black tracking-tight text-slate-900 tabular-data`}>{value}</div>
            <div className="text-xs text-slate-500 mt-1">{sub}</div>
        </div>
    );
}

function MiniStat({ label, value, accent, danger }) {
    return (
        <div className={`p-4 border ${accent ? "bg-slate-900 text-white border-slate-900" : danger ? "bg-red-50 border-red-300" : "bg-white border-slate-200"}`}>
            <div className={`text-[10px] uppercase tracking-widest font-semibold mb-1 ${accent ? "text-orange-300" : danger ? "text-red-700" : "text-slate-500"}`}>{label}</div>
            <div className="font-display text-xl font-black tabular-data">{value}</div>
        </div>
    );
}

function TechDashboard({ data, today, name }) {
    return (
        <div className="px-10 py-10 max-w-6xl mx-auto animate-fade-in" data-testid="tech-dashboard">
            <div className="mb-10">
                <div className="label-eyebrow mb-2">Vedoucí technik</div>
                <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Dobrý den{name ? `, ${name.split(" ")[0]}` : ""}.</h1>
                <p className="text-sm text-slate-600 mt-2">Provozní přehled staveb — zakázky, dnešní události a vaše úkoly.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Stat icon={Hammer} label="Zakázky v realizaci" value={(data.jobs_realizace || []).length} sub={`celkem ${data.jobs_total}`} testid="tech-stat-jobs" />
                <Stat icon={CalendarClock} label="Události dnes a zítra" value={today?.events?.length ?? 0} sub="plánovač" testid="tech-stat-events" />
                <Stat icon={ShieldAlert} label="Moje úkoly" value={(data.my_tasks || []).length} sub={`${data.tasks_open} otevřených ve firmě`} testid="tech-stat-tasks" />
            </div>
            <div className="mb-6" data-testid="tech-attendance"><Attendance jobs={data.jobs_realizace || []} /></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200">
                    <div className="px-6 py-3 border-b-2 border-slate-900 font-display font-bold uppercase tracking-wider text-sm">Stavby v realizaci</div>
                    {(data.jobs_realizace || []).length === 0 ? <div className="p-8 text-sm text-slate-400 text-center">Žádná zakázka v realizaci.</div> :
                        (data.jobs_realizace || []).map((j) => (
                            <Link key={j.id} to={`/zakazky/${j.id}`} className="flex items-center justify-between px-6 py-3 border-b border-slate-100 hover:bg-orange-50/40" data-testid={`tech-job-${j.id}`}>
                                <div><div className="font-medium text-slate-900">{j.title}</div><div className="text-xs text-slate-500">{j.site_address || "—"}</div></div>
                                <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${statusBadge(j.status)}`}>{statusLabel(j.status)}</span>
                            </Link>
                        ))}
                </div>
                <div className="space-y-6">
                    <div className="bg-slate-900 text-white border-l-4 border-orange-500">
                        <div className="px-6 py-3 border-b border-slate-800 font-display font-bold uppercase tracking-wider text-sm">Dnes a zítra</div>
                        <div className="p-5 text-sm">
                            {!today || today.events.length === 0 ? <div className="text-slate-500 text-xs">Žádné události</div> :
                                today.events.map((e) => (
                                    <Link key={e.id} to="/planovac" className="block mb-1.5 hover:text-orange-300">
                                        <span className={`text-[10px] font-bold uppercase mr-1.5 ${e.day === "today" ? "text-orange-400" : "text-slate-400"}`}>{e.day === "today" ? "Dnes" : "Zítra"}</span>
                                        {e.title}{e.location ? <span className="text-slate-400 text-xs"> · {e.location}</span> : ""}
                                    </Link>
                                ))}
                        </div>
                    </div>
                    <div className="bg-white border border-slate-200">
                        <div className="px-6 py-3 border-b-2 border-slate-900 font-display font-bold uppercase tracking-wider text-sm">Moje úkoly</div>
                        {(data.my_tasks || []).length === 0 ? <div className="p-8 text-sm text-slate-400 text-center">Nemáte žádné otevřené úkoly.</div> :
                            (data.my_tasks || []).map((t) => (
                                <Link key={t.id} to="/ukoly" className="block px-6 py-2.5 border-b border-slate-100 text-sm hover:bg-orange-50/40">
                                    {t.overdue && <span className="text-[10px] font-bold uppercase text-red-600 mr-1.5">Po termínu</span>}
                                    {t.title}{t.due_date ? <span className="text-slate-400 text-xs"> · do {new Date(t.due_date).toLocaleDateString("cs-CZ")}</span> : ""}
                                </Link>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
